// StoreHoursService — the venue's "pause online ordering" switch and dated
// closures, read from the orders worker's PUBLIC store-hours doc (written by
// the client center / tills; spec: pos-mvp/STORE-HOURS.md).
//
//   GET {API_BASE}/store-hours?appId&storeId
//   → { timezone, hours, closures (upcoming), website: {paused, reason,
//       resumeAt, because}, … }
//
// The worker resolves `website` for RIGHT NOW — the client never evaluates
// closures to decide whether it is paused; `closures` is only used to show
// an upcoming closure. Everything here FAILS OPEN: no doc (network down,
// worker error, odd payload) = not paused, exactly as before this existed.
// The pure helpers (activePause, describePause, upcomingClosureNotice) take
// `now` so they're unit-testable.

import { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_LOYALTY_API_BASE || "https://api.dineplatform.com";

/** UK venues; used only when the doc carries no (or an invalid) timezone.
 *  Never the device's zone — a customer abroad must see the venue's clock. */
export const DEFAULT_TIMEZONE = "Europe/London";

export interface WebsitePause {
  paused: boolean;
  reason: string | null;
  resumeAt: string | null;
  because: "closure" | "switch" | null;
}

export interface StoreClosure {
  id: string;
  from: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD (inclusive)
  fromTime?: string; // HH:mm
  toTime?: string; // HH:mm
  note?: string;
  channels?: ("online" | "platforms")[];
}

export interface StoreHoursDoc {
  timezone?: string;
  hours?: any;
  closures?: StoreClosure[];
  website?: WebsitePause | null;
  updatedAt?: any;
}

// ── Fetch + cache ─────────────────────────────────────────────────────────

const cache = new Map<string, StoreHoursDoc>();
const cacheKey = (appId: string, storeId: string) => `${appId}:${storeId}`;

/** Fetch the doc. Throws on any failure — callers fail open. */
export async function fetchStoreHours(appId: string, storeId: string): Promise<StoreHoursDoc> {
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? window.setTimeout(() => ctrl.abort(), 10_000) : 0;
  try {
    const res = await fetch(
      `${API_BASE}/store-hours?appId=${encodeURIComponent(appId)}&storeId=${encodeURIComponent(storeId)}`,
      { cache: "no-store", signal: ctrl?.signal }
    );
    if (!res.ok) throw new Error(`store-hours ${res.status}`);
    const doc = await res.json();
    if (!doc || typeof doc !== "object") throw new Error("store-hours: bad payload");
    cache.set(cacheKey(appId, storeId), doc);
    return doc as StoreHoursDoc;
  } finally {
    if (timer) window.clearTimeout(timer);
  }
}

/** The store-hours doc for this store, re-fetched every 60 s and whenever
 *  the tab comes back (same rhythm as the opening-hours sweep). null =
 *  unknown (loading, disabled, or the last fetch failed) → not paused. */
export function useStoreHours(appId: string | undefined, storeId: string | undefined, enabled: boolean): StoreHoursDoc | null {
  const key = appId && storeId ? cacheKey(appId, storeId) : "";
  const [doc, setDoc] = useState<StoreHoursDoc | null>(() => (key && cache.get(key)) || null);

  useEffect(() => {
    if (!enabled || !appId || !storeId) { setDoc(null); return; }
    setDoc(cache.get(cacheKey(appId, storeId)) || null);
    let live = true;
    const check = async () => {
      try {
        const next = await fetchStoreHours(appId, storeId);
        if (live) setDoc((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      } catch {
        // Fail OPEN — a network hiccup must never block ordering.
        cache.delete(cacheKey(appId, storeId));
        if (live) setDoc(null);
      }
    };
    void check();
    const t = window.setInterval(check, 60_000);
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      live = false;
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [appId, storeId, enabled]);

  return doc;
}

// ── Pure helpers ──────────────────────────────────────────────────────────

/** The pause in force, or null. Fails open on a missing/odd doc, and drops
 *  a pause whose resumeAt has already passed (the doc can be up to a minute
 *  old — the worker would no longer say paused). */
export function activePause(doc: StoreHoursDoc | null | undefined, now: number = Date.now()): WebsitePause | null {
  const w = doc?.website;
  if (!w || w.paused !== true) return null;
  if (w.resumeAt) {
    const t = Date.parse(w.resumeAt);
    if (!isNaN(t) && t <= now) return null;
  }
  return {
    paused: true,
    reason: typeof w.reason === "string" && w.reason.trim() ? w.reason.trim() : null,
    resumeAt: w.resumeAt || null,
    because: w.because === "closure" ? "closure" : w.because === "switch" ? "switch" : null,
  };
}

const validZone = (tz: string | undefined): string => {
  if (!tz) return DEFAULT_TIMEZONE;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
    return tz;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

type ZonedParts = { date: string; time: string; weekday: string; day: number; month: string; monthLong: string };

/** Calendar date / time of an instant in the venue's zone. */
export function zonedParts(at: number, timezone: string): ZonedParts {
  const tz = validZone(timezone);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(at));
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "";
  const y = get("year"), m = get("month"), d = get("day");
  const hh = get("hour") === "24" ? "00" : get("hour");
  const date = `${y}-${m}-${d}`;
  return { date, time: `${hh}:${get("minute")}`, ...dateWords(date) };
}

/** "18:30" → "6:30 PM", "11:00" → "11 AM" — the same style as the site's "opens 11 AM" labels (TimeService.formatMinutes). */
export function time12(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h24 = Number(m[1]) % 24, mm = m[2];
  const ampm = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return mm === "00" ? `${h12} ${ampm}` : `${h12}:${mm} ${ampm}`;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** Words for a plain calendar date (zone-free: it IS the venue's date). */
function dateWords(date: string): { weekday: string; day: number; month: string; monthLong: string } {
  const [y, m, d] = date.split("-").map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return { weekday: WEEKDAYS[wd], day: d, month: MONTHS[m - 1], monthLong: MONTHS_LONG[m - 1] };
}

const dayNumber = (date: string): number => {
  const [y, m, d] = date.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
};

/** "today" / "tomorrow" / "Thu" (this week) / "Thu 25 Dec". */
function dayWord(date: string, todayDate: string): string {
  const diff = dayNumber(date) - dayNumber(todayDate);
  if (diff <= 0) return "today";
  if (diff === 1) return "tomorrow";
  const w = dateWords(date);
  if (diff < 7) return w.weekday;
  return `${w.weekday} ${w.day} ${w.month}`;
}

/** "Back at 18:30" / "Back tomorrow 11:00" / "Back tomorrow" (a closure
 *  that ends at midnight) / "Back Thu 11:00" / "Back Thu 25 Dec" — in the
 *  venue's zone. null when there's no (valid) resumeAt. */
export function backLabel(resumeAt: string | null | undefined, timezone: string, now: number = Date.now()): string | null {
  if (!resumeAt) return null;
  const t = Date.parse(resumeAt);
  if (isNaN(t)) return null;
  const at = zonedParts(t, timezone);
  const today = zonedParts(now, timezone);
  const word = dayWord(at.date, today.date);
  const midnight = at.time === "00:00";
  if (word === "today") return `Back at ${time12(at.time)}`;
  return midnight ? `Back ${word}` : `Back ${word} ${time12(at.time)}`;
}

export interface PauseDescription {
  because: "closure" | "switch" | null;
  /** Banner headline. */
  title: string;
  /** The venue's own note, as typed. */
  reason: string | null;
  /** "Back at 18:30" etc., or null (until staff resume). */
  back: string | null;
  /** Short status for the mode chip / chooser — replaces "Closed · opens …". */
  status: string;
  /** Disabled checkout button text. */
  button: string;
  /** One-line message for add-to-cart / checkout refusals. */
  message: string;
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const sentence = (s: string) => (/[.!?]$/.test(s) ? s : `${s}.`);

export function describePause(website: WebsitePause, timezone: string, now: number = Date.now()): PauseDescription {
  const closure = website.because === "closure";
  const title = closure ? "We're closed for online orders right now" : "Online ordering is paused right now";
  const reason = website.reason && website.reason.trim() ? website.reason.trim() : null;
  const back = backLabel(website.resumeAt, timezone, now);
  const status = `${closure ? "Closed" : "Paused"}${back ? ` · ${lowerFirst(back)}` : ""}`;
  const message = [sentence(title), reason ? sentence(reason) : "", back ? sentence(back) : ""].filter(Boolean).join(" ");
  return {
    because: website.because,
    title,
    reason,
    back,
    status,
    button: closure ? "Closed for orders" : "Ordering paused",
    message,
  };
}

/** A heads-up for the next closure that affects the website and starts
 *  within `withinDays` (and hasn't started yet — one in force is the
 *  worker's `website` pause): "Closed Thu 25 Dec – Fri 26 Dec · Back on 27
 *  December", "Closed today from 15:00", "Closed tomorrow 15:00–18:00".
 *  null when there's nothing to say. */
export function upcomingClosureNotice(
  closures: StoreClosure[] | null | undefined,
  timezone: string,
  now: number = Date.now(),
  withinDays = 7
): string | null {
  if (!Array.isArray(closures) || closures.length === 0) return null;
  const today = zonedParts(now, timezone);
  const nowKey = `${today.date}T${today.time}`;
  const isDate = (s: any) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
  const isTime = (s: any) => typeof s === "string" && /^\d{2}:\d{2}$/.test(s);
  const candidates = closures
    .filter((c) => c && isDate(c.from))
    .filter((c) => !Array.isArray(c.channels) || c.channels.length === 0 || c.channels.includes("online"))
    .map((c) => ({ c, startKey: `${c.from}T${isTime(c.fromTime) ? c.fromTime : "00:00"}` }))
    .filter(({ startKey }) => startKey > nowKey)
    .filter(({ c }) => dayNumber(c.from) - dayNumber(today.date) < withinDays)
    .sort((a, b) => (a.startKey < b.startKey ? -1 : a.startKey > b.startKey ? 1 : 0));
  const next = candidates[0]?.c;
  if (!next) return null;

  const fromWord = dayWord(next.from, today.date);
  const to = isDate(next.to) && next.to! > next.from ? next.to! : null;
  const fromTime = isTime(next.fromTime) ? next.fromTime : null;
  const toTime = isTime(next.toTime) ? next.toTime : null;
  let when: string;
  if (to) {
    when = `${fromWord}${fromTime ? ` ${time12(fromTime)}` : ""} – ${dayWord(to, today.date)}${toTime ? ` ${time12(toTime)}` : ""}`;
  } else if (fromTime && toTime) {
    when = `${fromWord} ${time12(fromTime)}–${time12(toTime)}`;
  } else if (fromTime) {
    when = `${fromWord} from ${time12(fromTime)}`;
  } else if (toTime) {
    when = `${fromWord} until ${time12(toTime)}`;
  } else {
    when = fromWord;
  }
  const note = typeof next.note === "string" && next.note.trim() ? ` · ${next.note.trim()}` : "";
  return `Closed ${when}${note}`;
}
